import {
  computed,
  effect,
  inject,
  input,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Renderer2,
  Injector,
  untracked,
  signal,
} from '@angular/core';

import type { IconToken } from './directives/icon-color.directive';
import { IconColorDirective } from './directives/icon-color.directive';
import type { IconName } from './icons.type';
import { injectIconsContainerStrategy } from '../../icons-container/contracts';

export const IconSizes = {
  'small-xxx': 8,
  'small-xx': 12,
  'small-x': 16,
  small: 18,
  medium: 24,
  large: 32,
  'large-x': 48,
  'large-xx': 54,
};

export type IconSize = keyof typeof IconSizes;

@Component({
  standalone: true,
  selector: 'rt-icon',
  templateUrl: './icon.component.html',
  styleUrls: ['./icon.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.icon]': 'true',
    '[class.has-auto-height]': 'hasAutoHeight()',
    '[style.height]': 'size()',
    '[style.width]': 'size()',
  },
  hostDirectives: [
    {
      directive: IconColorDirective,
      inputs: ['iconColor: colorToken'],
    },
  ],
})
export class IconComponent {
  private readonly iconsContainerStrategy = injectIconsContainerStrategy({ optional: true });
  private readonly renderer = inject(Renderer2);
  private readonly injector = inject(Injector);

  public readonly elementRef = inject(ElementRef);

  public name = input<IconName>('information');
  public hasAutoHeight = input(false);
  public colorToken = input<IconToken | undefined>();
  public size = input<`${number}px`, IconSize>(`${IconSizes['small']}px`, {
    transform: value => `${IconSizes[value]}px`,
  });

  public svgName = computed(() => `#${this.name()}`);
  public isLoaded = signal(false);

  private watchClass = effect(onCleanup => {
    const className = `icon--${this.name()}`;
    this.renderer.addClass(this.elementRef.nativeElement, className);
    onCleanup(() => this.renderer.removeClass(this.elementRef.nativeElement, className));
  });

  private loadIcon = effect(() => {
    if (!this.iconsContainerStrategy) return;

    const name = this.name();
    this.isLoaded.set(false);
    untracked(() =>
      this.iconsContainerStrategy!.load(name, this.elementRef.nativeElement, this.injector),
    ).then(() => this.isLoaded.set(true));
  });
}
